import { OnboardStudentContext } from '@dfh-corp-sites/onboarding/context'
import { first, firstValueFrom, tap } from 'rxjs'
import { assign, createMachine, Event, InterpreterFrom, StateConfig, StateFrom } from 'xstate'

import { persistedStudentState$, setStudentPersistedState } from '../hooks/useOnboardStudentPersist'

export type OnboardStudentEvent =
  | { type: 'CONTINUE' }
  | { type: 'BACK' }
  | { type: 'RESTORE' }
  | { type: 'UPDATE_FIELDS'; data: Partial<OnboardStudentContext> }

export type OnboardStudentState = {
  value:
    | 'initial'
    | 'createAccount'
    | 'createAccountPersist'
    | 'enrollment'
    | 'enrollmentPersist'
    | 'address'
    | 'addressPersist'
    | 'invalid'
    | 'complete'
  context: OnboardStudentContext
  changed?: boolean
  done?: boolean
}

export type OnboardStudentMachine = typeof machine
export type OnboardStudentInterpreter = InterpreterFrom<OnboardStudentMachine>
export type OnboardStudentObserverCallback = (state: OnboardStudentStateObject) => void
export type OnboardStudentStateConfig = StateConfig<
  OnboardStudentContext & { customerId?: string },
  OnboardStudentEvent
>
export type OnboardStudentStateObject = StateFrom<OnboardStudentMachine>

export type OnboardStudentStateValue = OnboardStudentState['value']

export interface OnboardStudentClaimParsed {
  state: OnboardStudentStateValue
  context: OnboardStudentContext
}

/**
 * Persist state and context (for use with `invoke.src`)
 */
const persistAsState =
  (stateToRecord: Extract<OnboardStudentStateValue, 'enrollment' | 'address' | 'complete'>) =>
  (context: OnboardStudentContext) =>
  (send: (event: Event<OnboardStudentEvent>) => void) => {
    setStudentPersistedState({
      state: stateToRecord,
      context,
    })
    return firstValueFrom(
      persistedStudentState$.pipe(
        first((state) => state.loading === false),
        tap((state) => {
          if (state.result?._tag === 'Right') {
            send({ type: 'CONTINUE' })
          } else {
            send({ type: 'RESTORE' })
          }
        }),
      ),
    )
  }

export const machine = createMachine<
  OnboardStudentContext & { customerId?: string },
  OnboardStudentEvent,
  OnboardStudentState
>({
  predictableActionArguments: true,
  id: 'onboard_student',
  initial: 'initial',
  context: {
    firstName: '',
    lastName: '',
    phone: '',
    graduationDate: '',
    universityName: '',
    specialty: '',
    certificateUrl: '',
    address: {
      street: [],
      city: '',
      state: '',
      zip: '',
      countryCode: '',
    },
  },
  states: {
    initial: {
      on: {
        '*': [
          {
            cond: ({ customerId }) => Boolean(customerId),
            target: 'complete',
          },
          {
            cond: ({ customerId }) => !customerId,
            target: 'createAccount',
          },
        ],
      },
    },
    createAccount: {
      on: {
        CONTINUE: 'createAccountPersist',
      },
    },
    createAccountPersist: {
      entry: 'clearPersistError',
      invoke: {
        src: persistAsState('enrollment'),
        onError: {
          target: 'createAccount',
          actions: 'handlePersistError',
        },
      },
      on: {
        CONTINUE: {
          target: 'enrollment',
        },
        RESTORE: {
          target: 'createAccount',
          actions: 'handlePersistError',
        },
      },
    },
    enrollment: {
      on: {
        CONTINUE: 'enrollmentPersist',
        BACK: { target: 'createAccount', actions: 'clearPersistError' },
      },
    },
    enrollmentPersist: {
      entry: 'clearPersistError',
      invoke: {
        src: persistAsState('address'),
        onError: {
          target: 'enrollment',
          actions: 'handlePersistError',
        },
      },
      on: {
        CONTINUE: {
          target: 'address',
        },
        RESTORE: {
          target: 'enrollment',
          actions: 'handlePersistError',
        },
      },
    },
    address: {
      on: {
        CONTINUE: 'addressPersist',
        BACK: { target: 'enrollment', actions: 'clearPersistError' },
      },
    },
    addressPersist: {
      entry: 'clearPersistError',
      invoke: {
        src: persistAsState('complete'),
        onError: {
          target: 'address',
          actions: 'handlePersistError',
        },
      },
      on: {
        CONTINUE: {
          target: 'complete',
        },
        RESTORE: {
          target: 'address',
          actions: 'handlePersistError',
        },
      },
    },
    invalid: {
      on: {
        RESTORE: 'complete',
      },
    },
    complete: {
      entry: 'submit',
      type: 'final',
    },
  },
  on: {
    UPDATE_FIELDS: {
      actions: assign((context, event) => ({
        ...context,
        ...(event.type === 'UPDATE_FIELDS' ? event.data : {}),
      })),
    },
  },
})

export const createOnboardStudentMachine = () => machine
